import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useFileUpload } from '@/hooks/use-upload';
import { EventForm } from '@/components/sendEvent/EventForm';
import { useApp } from '@/context/AppContext';
import Header from '@/components/ui/header';
import { TypeNiche } from '@/types';
import Footer from '@/components/ui/footer';
import { useDebounce } from '@/hooks/use-debounce';

const SendEvent = () => {
  const { user, logout } = useAuth();
  const { listTypeNiche } = useApp();
  const navigate = useNavigate();
  const { handleUpload, isUploading } = useFileUpload();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [pixelId, setPixelId] = useState('');
  const [token, setToken] = useState('');
  const [userName, setUserName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [typeNiche, setTypeNiche] = useState<TypeNiche>(null);
  const [isLoadingNiches, setIsLoadingNiches] = useState(true);
  const [localTypeNicheName, setLocalTypeNicheName] = useState(typeNiche?.name || '');
  const debouncedTypeNicheName = useDebounce(localTypeNicheName, 300);

  useEffect(() => {
    if (listTypeNiche.length > 0) {
      setIsLoadingNiches(false);
      setTypeNiche((prev) =>
        prev && listTypeNiche.some((niche) => niche.name === prev.name) ? prev : listTypeNiche[0],
      );
      setLocalTypeNicheName((prev) =>
        prev && listTypeNiche.some((niche) => niche.name === prev)
          ? prev
          : listTypeNiche[0]?.name || '',
      );
    } else {
      setIsLoadingNiches(true);
    }
  }, [listTypeNiche]);

  useEffect(() => {
    if (debouncedTypeNicheName) {
      const found = listTypeNiche.find((item) => item.name === debouncedTypeNicheName);
      if (found) {
        setTypeNiche(found);
      }
    }
  }, [debouncedTypeNicheName, listTypeNiche]);

  const handleSubmit = async () => {
    if (!pixelId || !token || !email) return;
    await handleUpload({
      file: typeNiche?.name === 'Custom' ? selectedFile : null,
      pixelId,
      token,
      userName,
      email,
      typeNiche,
    })
      .then(() => {
        setSelectedFile(null);
        setPixelId('');
        setToken('');
      })
      .catch((error) => {
        if (
          error?.message?.includes('Sessão expirada') ||
          error?.message?.includes('Invalid or expired token')
        ) {
          logout();
          navigate('/');
        }
      });
  };

  const isFormValid =
    typeNiche?.name === 'Custom'
      ? selectedFile && pixelId.trim() !== '' && token.trim() !== ''
      : pixelId.trim() !== '' && token.trim() !== '';

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-secondary to-background">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-5xl mx-auto space-y-6">
          <div className="space-y-4">
            <EventForm
              userName={userName}
              email={email}
              pixelId={pixelId}
              token={token}
              typeNiche={localTypeNicheName}
              listTypeNiche={listTypeNiche}
              onUserNameChange={setUserName}
              onEmailChange={setEmail}
              onPixelIdChange={setPixelId}
              onTokenChange={setToken}
              onTypeNicheChange={setLocalTypeNicheName}
              isSubmitting={isUploading}
              isValid={isFormValid}
              file={selectedFile}
              onFileSelect={setSelectedFile}
              onSubmit={handleSubmit}
              isLoadingNiches={isLoadingNiches}
            />
          </div>
        </div>
      </main>
      <div className="bottom-0 left-0 right-0">
        <Footer />
      </div>
    </div>
  );
};

export default SendEvent;
